import React, { useState, useEffect, useMemo } from 'react';
import { Search, Globe, ExternalLink, Calendar, DollarSign, RefreshCw, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../api';
import toast from 'react-hot-toast';
import '../styles/DomainsScreen.css';

const DomainsScreen = ({ user }) => {
    const [domains, setDomains] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedDomain, setExpandedDomain] = useState(null);

    const fetchDomains = async () => {
        setIsLoading(true);
        try {
            const data = await api.getAllDomains();
            setDomains(data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDomains();
    }, []);

    const filteredDomains = useMemo(() => {
        return domains.filter(domain => {
            const matchesSearch = 
                domain.domain_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                domain.property_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                domain.property_code?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesStatus = statusFilter === 'all' || domain.status === statusFilter;
            
            return matchesSearch && matchesStatus;
        });
    }, [domains, searchTerm, statusFilter]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatAddress = (domain) => {
        return [domain.address, domain.city, domain.state, domain.zip_code]
            .filter(part => part)
            .join(', ');
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return 'status-active';
            case 'CANCELED': return 'status-canceled';
            case 'PAST_DUE': return 'status-past-due';
            default: return 'status-pending';
        }
    };

    const toggleExpand = (domainId) => {
        setExpandedDomain(expandedDomain === domainId ? null : domainId);
    };

    if (isLoading) return <div className="domains-page"><p>Loading domains...</p></div>;
    if (error) return <div className="domains-page"><div className="error-message">{error}</div></div>;

    return (
        <div className="domains-page">
            <div className="domains-header">
                <h1 className="page-title">Domains / Websites</h1>
                <p className="page-subtitle">Manage domain registrations and website settings</p>
            </div>

            <div className="domains-filters">
                <div className="search-input-container">
                    <Search size={18} className="search-icon" />
                    <input 
                        type="text" 
                        placeholder="Search domains or properties..." 
                        className="search-input" 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                    />
                </div>
                <div className="filter-group">
                    <label>Status:</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="all">All Status</option>
                        <option value="ACTIVE">Active</option>
                        <option value="CANCELED">Canceled</option>
                        <option value="PAST_DUE">Past Due</option>
                    </select>
                </div>
            </div>

            {filteredDomains.length === 0 ? (
                <div className="empty-state">
                    <Globe size={48} className="empty-icon" />
                    <h3>No domains found</h3>
                    <p>Domains will appear here once they are registered through billing.</p>
                </div>
            ) : (
                <div className="domains-grid">
                    {filteredDomains.map(domain => (
                        <div key={domain.id} className="domain-card">
                            <div className="domain-card-header" onClick={() => toggleExpand(domain.id)}>
                                <div className="domain-main-info">
                                    <div className="domain-icon">
                                        <Globe size={24} />
                                    </div>
                                    <div className="domain-title">
                                        <h3 className="domain-name">{domain.domain_name}</h3>
                                        <span className="domain-property">{domain.property_name} ({domain.property_code})</span>
                                    </div>
                                </div>
                                <div className="domain-status-section">
                                    <span className={`status-badge ${getStatusColor(domain.status)}`}>
                                        {domain.status}
                                    </span>
                                    {expandedDomain === domain.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                </div>
                            </div>

                            {expandedDomain === domain.id && (
                                <div className="domain-card-content">
                                    <div className="domain-details-grid">
                                        <div className="detail-section">
                                            <h4>Registration Details</h4>
                                            <div className="detail-row">
                                                <span className="detail-label">Domain Name</span>
                                                <span className="detail-value">
                                                    {domain.domain_name}
                                                    <a href={`http://${domain.domain_name}`} target="_blank" rel="noopener noreferrer" className="external-link">
                                                        <ExternalLink size={14} />
                                                    </a>
                                                </span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">Product</span>
                                                <span className="detail-value">{domain.product_name}</span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">Start Date</span>
                                                <span className="detail-value">{formatDate(domain.start_date)}</span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">Next Billing</span>
                                                <span className="detail-value">{formatDate(domain.next_billing_date)}</span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">Price</span>
                                                <span className="detail-value">${parseFloat(domain.price).toFixed(2)}/{domain.billing_cycle === 'ANNUALLY' ? 'yr' : 'mo'}</span>
                                            </div>
                                        </div>

                                        <div className="detail-section">
                                            <h4>Property Information</h4>
                                            <div className="detail-row">
                                                <span className="detail-label">Property</span>
                                                <span className="detail-value">{domain.property_name}</span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">Code</span>
                                                <span className="detail-value">{domain.property_code}</span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">Address</span>
                                                <span className="detail-value">{formatAddress(domain) || 'N/A'}</span>
                                            </div>
                                            <div className="detail-row">
                                                <span className="detail-label">Customer</span>
                                                <span className="detail-value">{domain.customer_name}</span>
                                            </div>
                                        </div>

                                        <div className="detail-section">
                                            <h4>DNS Configuration</h4>
                                            <div className="dns-placeholder">
                                                <Settings size={20} />
                                                <span>DNS settings will be available here</span>
                                            </div>
                                        </div>

                                        <div className="detail-section">
                                            <h4>Website Design</h4>
                                            <div className="design-placeholder">
                                                <Globe size={20} />
                                                <span>Website design status will be shown here</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="domain-actions">
                                        <button className="action-btn renew">
                                            <RefreshCw size={16} />
                                            <span>Renew</span>
                                        </button>
                                        <button className="action-btn settings">
                                            <Settings size={16} />
                                            <span>Settings</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DomainsScreen;
